import { ILocation } from '@rbi-ctg/frontend';
import { IDeliveryAddress, IEditDeliveryAddressInput } from 'generated/graphql-gateway';
import type { PlacesSessionToken } from 'hooks/geolocation/types';
import { IErrorModal } from 'hooks/use-error-modal';
import { UserDetails } from 'state/auth/hooks/types';
import { t } from 'state/intl/translations';
import { FormValidationState } from 'utils/form';
import { IPlaceAddress, IPlaceData } from 'utils/geolocation';

export interface IDeliveryAddressFormProps {
  instructions?: string;
  user?: UserDetails | null;
  getDetailsPlaceData: (id: string, sessionToken?: PlacesSessionToken) => Promise<IPlaceData>;
  setCreateAddressView: (setView: boolean) => void;
  showDeliveryAddresses: boolean;
  setAddress: (address: SelectedDeliveryAddress) => void;
  isLoading: boolean;
  addressToEdit?: IDeliveryAddress | null;
  editDeliveryAddress?: (input: IEditDeliveryAddressInput) => Promise<any>;
  addressComponents?: IPlaceAddress | null;
  setAddressComponents: (address: IPlaceAddress | null) => void;
  message: string;
  setMessage: (message: string) => void;
  clearAddressBeingEdited?: () => void;
  deliveryAddresses: IDeliveryAddress[];
  setFormHasError?: (hasError: boolean) => void;
  shouldSaveInfoChecked: boolean;
  setShouldSaveInfoChecked: (shouldSave: boolean) => void;
  alias: string;
  setAlias: (alias: string) => void;
  interceptedClose?: boolean;
  setInterceptedClose: (val: boolean) => void;
  dispatch: (action: AddressModalAction) => void;
  setShowtermsLink: (show: boolean) => void;
}

export interface IPhoneAddressValidation {
  phoneNumber: FormValidationState;
  address: FormValidationState;
}

export enum ActionStatus {
  ERROR = 'ERROR',
  IDLE = 'IDLE',
  PENDING = 'PENDING',
  QUOTE_UNAVAILABLE = 'QUOTE_UNAVAILABLE',
  SUCCESS = 'SUCCESS',
  UPDATE = 'UPDATE',
}

export type SelectedDeliveryAddress = {
  address: IPlaceAddress;
  phoneNumber: string;
  deliveryInstructions?: string;
  placeId?: string;
  coordinates?: ILocation;
  shouldSaveAddress?: boolean;
};

export type AddressModalAction =
  | { type: ActionStatus.IDLE }
  | { type: ActionStatus.QUOTE_UNAVAILABLE }
  | { type: ActionStatus.SUCCESS }
  | { type: ActionStatus.ERROR; error: IErrorModal }
  | { type: ActionStatus.PENDING; address: SelectedDeliveryAddress };

export interface AddressModalState {
  address: SelectedDeliveryAddress | null;
  error: IErrorModal | null;
  status: ActionStatus;
}

// Alias enum
export enum AliasLabel {
  Home = 'Home',
  Work = 'Work',
  Other = 'Other',
}

// Alias key values
export const AliasKey = {
  Home: 'Home',
  Work: 'Work',
  Other: 'Other',
} as const;

export const getAliasLabel = (): Record<AliasLabel, string> => ({
  Home: t('aliasLabelHome'),
  Work: t('aliasLabelWork'),
  Other: t('aliasLabelOther'),
});

// List of alias options (used for iteration)
export const aliasOptions = [AliasLabel.Home, AliasLabel.Work, AliasLabel.Other] as const;

// Icon name mapping
export const aliasIcons: Record<AliasLabel, string> = {
  [AliasLabel.Home]: 'home',
  [AliasLabel.Work]: 'work',
  [AliasLabel.Other]: 'location',
};

// Icon size mapping
export const aliasIconsSize: Record<AliasLabel, string> = {
  [AliasLabel.Home]: '4',
  [AliasLabel.Work]: '3',
  [AliasLabel.Other]: '4',
};

export enum FormStatus {
  Select = 'select',
  Add = 'add',
  Edit = 'edit',
  Unavailable = 'unavailable',
}

export enum DeliveryAddressFieldValidation {
  Unit = 'unit',
  Phone = 'phone',
  Instructions = 'instructions',
}

export interface DeliveryAddressFormHeaderProps {
  isEditing: boolean;
  setCreateAddressView: (show: boolean) => void;
  showDeliveryAddresses: boolean;
}
